import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { assertPresent, compareByString, isPresent } from '@luminovo/commons';
import {
    colorSystem,
    DestructiveTertiaryIconButton,
    Flexbox,
    MenuItem,
    SearchInput,
    SecondaryButton,
    TertiaryButton,
    Text,
    Tooltip,
} from '@luminovo/design-system';
import { transEnum } from '@luminovo/frontend-utils';
import { Badge, ListItem, Menu, styled } from '@mui/material';
import { useSelector } from '@tanstack/react-store';
import { ChevronDownIcon, ListFilterIcon, PlusIcon, XIcon } from 'lucide-react';
import React from 'react';

import {
    FilterArrIncludesSome,
    FilterArrIncludesSomeField,
    FilterEnumEqualsAny,
    FilterEnumEqualsAnyField,
    FilterIncludesString,
    FilterInDateRange,
    FilterInMonetaryValueRange,
    FilterInNumberRange,
    FilterInQuantityRange,
    FilterInRelativeDateRange,
} from '../Filters';
import { isFilterValue } from '../Filters/createFilterValueUpdater';
import { Column, ColumnFilter, RowData, Table } from '../tanstackTypes';
import { FilterFnByDataType, FilterFnOptionTranslations, getFilterConfig, SupportedFilterFn } from '../type';

export type FilterMenuState<TData extends RowData> =
    | {
          type: 'None';
          anchorEl: undefined;
      }
    | {
          type: 'AddFilter';
          anchorEl: Element;
      }
    | {
          type: 'ColumnFilter';
          anchorEl: Element;
          column: Column<TData, unknown>;
      }
    | {
          type: 'FilterManager';
          anchorEl: Element;
      };

export function FilterMenu<TData extends RowData>({ table }: { table: Table<TData> }): JSX.Element {
    const [menuState, setMenuState] = React.useState<FilterMenuState<TData>>({ type: 'None', anchorEl: undefined });

    const filterCount = table.getAllLeafColumns().filter((col) => col.getIsFiltered()).length;
    const hasFiltersEnabled = table.getAllLeafColumns().some((col) => col.getCanFilter());

    const handleOnClick = React.useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            if (menuState.type !== 'None') {
                return;
            }
            if (filterCount === 0) {
                setMenuState({ type: 'AddFilter', anchorEl: e.currentTarget });
            } else {
                setMenuState({ type: 'FilterManager', anchorEl: e.currentTarget });
            }
        },
        [menuState, filterCount, setMenuState],
    );

    const handleOpenAddFilterMenu = () => {
        if (menuState.type === 'None') {
            return;
        }
        setMenuState({ type: 'AddFilter', anchorEl: menuState.anchorEl });
    };

    const handleOpenColumnFilter = (column: Column<TData, unknown>) => {
        if (menuState.type === 'None') {
            return;
        }
        setMenuState({ type: 'ColumnFilter', anchorEl: menuState.anchorEl, column });
    };

    const handleClose = () => {
        table.getAllLeafColumns().forEach((column) => {
            const filterValue = column.getFilterValue();
            if (isFilterValue(filterValue)) {
                if (Array.isArray(filterValue.value) && filterValue.value.length === 0) {
                    column.setFilterValue(undefined);
                } else if (filterValue.value === '') {
                    column.setFilterValue(undefined);
                }
            }
        });

        setMenuState({ type: 'None', anchorEl: undefined });
    };

    if (!hasFiltersEnabled) {
        return <></>;
    }

    return (
        <>
            <Badge badgeContent={filterCount} color={'primary'}>
                <SecondaryButton size={'medium'} onClick={handleOnClick} startIcon={<ListFilterIcon />}>
                    <Trans>Filter</Trans>
                </SecondaryButton>
            </Badge>

            <Menu
                anchorEl={menuState.anchorEl}
                open={menuState.type === 'FilterManager'}
                onClose={handleClose}
                variant="menu"
                keepMounted
            >
                <FilterManager table={table} onClose={handleClose} />
                <AddFilterMenuItem
                    table={table}
                    menuState={menuState}
                    onClick={handleOpenAddFilterMenu}
                    onColumnFilter={handleOpenColumnFilter}
                    onClose={handleClose}
                />
                <DeleteFilterMenuItem table={table} onClose={handleClose} />
            </Menu>

            <ColumFilterMenu menuState={menuState} onClose={handleClose} table={table} />
        </>
    );
}

function FilterManager<TData extends RowData>({
    table,
    onClose,
}: {
    table: Table<TData>;
    onClose: () => void;
}): JSX.Element {
    const filters = useSelector(table.store, (state) => state.columnFilters);

    return (
        <ListItem
            style={{ backgroundColor: colorSystem.neutral.white, padding: '8px' }}
            divider={true}
            onKeyDown={(e) => e.stopPropagation()}
        >
            <Flexbox
                gap={8}
                sx={{
                    flexDirection: 'column',
                }}
            >
                {filters.map((columFilter) => (
                    <FilterManagerItem key={columFilter.id} columFilter={columFilter} table={table} onClose={onClose} />
                ))}
                {filters.length === 0 && (
                    <Text variant={'body-small'} color={colorSystem.neutral[6]}>
                        {t`No filters applied`}
                    </Text>
                )}
            </Flexbox>
        </ListItem>
    );
}

function FilterManagerItem<TData extends RowData>({
    columFilter,
    table,
    onClose,
}: {
    columFilter: ColumnFilter;
    table: Table<TData>;
    onClose: () => void;
}) {
    const sharedContext = table.options.meta?.sharedContext;
    const column = table.getColumn(columFilter.id);
    if (!column) return null;
    const onlyHasOneFilter = table.getAllLeafColumns().filter((col) => col.getIsFiltered()).length === 1;
    const rawLabel = column.columnDef.meta?.label() ?? t`Unknown`;
    const parentColumnId = column.columnDef.meta?.parentColumnId;
    const parentLabel = parentColumnId ? table.getColumn(parentColumnId)?.columnDef.meta?.label() : undefined;
    const label = parentLabel ? `${parentLabel} > ${rawLabel}` : rawLabel;

    const handleRemoveFilter = () => {
        column.setFilterValue(undefined);
        if (onlyHasOneFilter) {
            onClose();
        }
    };

    return (
        <Flexbox
            gap={12}
            sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
            }}
        >
            <Flexbox
                sx={{
                    justifyContent: 'space-between',
                    minWidth: '160px',
                    width: '100%',
                    alignItems: 'center',
                }}
            >
                {isPresent(getFilterConfig(column, 'text')) && (
                    <Flexbox
                        gap={8}
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        <Text variant={'body-small'} style={{ whiteSpace: 'nowrap' }}>{t`${label} contains`}</Text>
                        <FilterIncludesString column={column} onClose={onClose} />
                    </Flexbox>
                )}

                {isPresent(getFilterConfig(column, 'number')) && (
                    <Flexbox
                        gap={8}
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        <Text variant={'body-small'} style={{ whiteSpace: 'nowrap' }}>{t`${label} is between`}</Text>
                        <FilterInNumberRange column={column} onClose={onClose} />
                    </Flexbox>
                )}
                {isPresent(getFilterConfig(column, 'monetaryValue')) && (
                    <Flexbox
                        gap={8}
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        <Text variant={'body-small'} style={{ whiteSpace: 'nowrap' }}>{t`${label} is between`}</Text>
                        <FilterInMonetaryValueRange column={column} onClose={onClose} />
                    </Flexbox>
                )}
                {isPresent(getFilterConfig(column, 'quantity')) && (
                    <Flexbox
                        gap={8}
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        <Text variant={'body-small'} style={{ whiteSpace: 'nowrap' }}>
                            {t`${label} is between`}
                        </Text>
                        <FilterInQuantityRange column={column} onClose={onClose} />
                    </Flexbox>
                )}
                {isPresent(getFilterConfig(column, 'date')) && (
                    <DateFilterManagerContent column={column} label={label} onClose={onClose} />
                )}
                {isPresent(getFilterConfig(column, 'enum')) && (
                    <Flexbox
                        gap={8}
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        <Text variant={'body-small'} style={{ whiteSpace: 'nowrap' }}>{t`${label} is`}</Text>
                        <FilterEnumEqualsAnyField column={column} sharedContext={sharedContext} />
                    </Flexbox>
                )}
                {isPresent(getFilterConfig(column, 'array')) && (
                    <Flexbox
                        gap={8}
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        <Text variant={'body-small'} style={{ whiteSpace: 'nowrap' }}>{t`${label} is`}</Text>
                        <FilterArrIncludesSomeField column={column} sharedContext={sharedContext} />
                    </Flexbox>
                )}
            </Flexbox>
            <Tooltip title={t`Remove filter rule`} placement="left">
                <DestructiveTertiaryIconButton
                    aria-label={t`Remove filter rule`}
                    size={'small'}
                    onClick={handleRemoveFilter}
                >
                    <XIcon />
                </DestructiveTertiaryIconButton>
            </Tooltip>
        </Flexbox>
    );
}

function DateFilterManagerContent<TData extends RowData>({
    column,
    label,
    onClose,
}: {
    column: Column<TData, unknown>;
    label: string;
    onClose: () => void;
}): JSX.Element {
    const filterValue = column.getFilterValue();
    const activeFilterFn = isFilterValue(filterValue) ? filterValue.filterFn : SupportedFilterFn.inDateRange;

    if (activeFilterFn === SupportedFilterFn.isInLastDays || activeFilterFn === SupportedFilterFn.isOlderThanDays) {
        const fnLabel = transEnum(activeFilterFn, FilterFnOptionTranslations);
        return (
            <Flexbox
                gap={8}
                sx={{
                    alignItems: 'center',
                }}
            >
                <Text variant={'body-small'} style={{ whiteSpace: 'nowrap' }}>{`${label} ${fnLabel}`}</Text>
                <FilterInRelativeDateRange column={column} onClose={onClose} />
            </Flexbox>
        );
    }

    return (
        <Flexbox
            gap={8}
            sx={{
                alignItems: 'center',
            }}
        >
            <Text variant={'body-small'} style={{ whiteSpace: 'nowrap' }}>{t`${label} is between`}</Text>
            <FilterInDateRange column={column} onClose={onClose} />
        </Flexbox>
    );
}

function AddFilterMenuItem<TData extends RowData>({
    table,
    menuState,
    onClick,
    onColumnFilter,
    onClose,
}: {
    table: Table<TData>;
    menuState: FilterMenuState<TData>;
    onClick: () => void;
    onColumnFilter: (column: Column<TData, unknown>) => void;
    onClose: () => void;
}): JSX.Element {
    const [query, setQuery] = React.useState<string>('');

    const allFilterableColumns = table.getAllLeafColumns().filter((col) => col.getCanFilter());

    const unfilteredColumns = allFilterableColumns.filter((col) => !col.getIsFiltered());

    const additionalFiltersByParent = new Map<string, Column<TData, unknown>[]>();
    for (const col of unfilteredColumns) {
        const parentId = col.columnDef.meta?.parentColumnId;
        if (col.columnDef.meta?.isAdditionalFilter && parentId) {
            const existing = additionalFiltersByParent.get(parentId) ?? [];
            existing.push(col);
            additionalFiltersByParent.set(parentId, existing);
        }
    }

    const allTopLevelColumns = allFilterableColumns
        .filter((col) => !col.columnDef.meta?.isAdditionalFilter)
        .sort((a, b) => compareByString(a.columnDef.meta?.label() ?? '', b.columnDef.meta?.label() ?? ''));

    const matchesQuery = (label: string) => label.toLocaleLowerCase().includes(query);

    const filteredEntries: Array<{ column: Column<TData, unknown>; label: string }> = [];
    for (const col of allTopLevelColumns) {
        const parentLabel = col.columnDef.meta?.label() ?? t`Unknown`;
        if (!col.getIsFiltered() && matchesQuery(parentLabel)) {
            filteredEntries.push({ column: col, label: parentLabel });
        }
        const children = additionalFiltersByParent.get(col.id);
        if (children) {
            for (const child of children) {
                const childLabel = child.columnDef.meta?.label() ?? t`Unknown`;
                const compositeLabel = `${parentLabel} > ${childLabel}`;
                if (matchesQuery(parentLabel) || matchesQuery(childLabel)) {
                    filteredEntries.push({ column: child, label: compositeLabel });
                }
            }
        }
    }

    return (
        <>
            <MenuItem label={t`Add filter`} onClick={onClick} startIcon={<PlusIcon />} />

            <Menu
                anchorEl={menuState.anchorEl}
                open={menuState.type === 'AddFilter'}
                onClose={onClose}
                variant="menu"
                keepMounted
            >
                <ListItem style={{ backgroundColor: colorSystem.neutral.white }} onKeyDown={(e) => e.stopPropagation()}>
                    <SearchInput
                        placeholder={t`Filter by...`}
                        value={query}
                        onChange={(value) => setQuery(value)}
                        onClear={() => setQuery('')}
                        style={{ backgroundColor: colorSystem.neutral.white }}
                    />
                </ListItem>

                {filteredEntries.map(({ column, label }) => (
                    <MenuItem
                        label={label}
                        key={column.id}
                        onClick={() => onColumnFilter(column)}
                        style={column.columnDef.meta?.isAdditionalFilter ? { paddingLeft: 32 } : undefined}
                    />
                ))}

                {filteredEntries.length === 0 && (
                    <ListItem style={{ backgroundColor: colorSystem.neutral.white }}>
                        <Text variant={'body-small'} color={colorSystem.neutral[6]}>
                            <Trans>No results</Trans>
                        </Text>
                    </ListItem>
                )}
            </Menu>
        </>
    );
}

export function ColumFilterMenu<TData extends RowData>({
    menuState,
    onClose,
    table,
}: {
    menuState: FilterMenuState<TData>;
    onClose: () => void;
    table: Table<TData>;
}): JSX.Element {
    const sharedContext = table.options.meta?.sharedContext;
    if (menuState.type !== 'ColumnFilter') {
        return <></>;
    }
    const { column } = menuState;

    return (
        <Menu
            anchorEl={menuState.anchorEl}
            open={menuState.type === 'ColumnFilter'}
            onClose={onClose}
            variant="menu"
            keepMounted
        >
            {isPresent(getFilterConfig(column, 'text')) && (
                <ListItem style={{ backgroundColor: colorSystem.neutral.white }}>
                    <Flexbox
                        gap={8}
                        sx={{
                            flexDirection: 'column',
                        }}
                    >
                        <FilterFunctionMenuItem column={column} />
                        <FilterIncludesString column={column} onClose={onClose} />
                    </Flexbox>
                </ListItem>
            )}
            {isPresent(getFilterConfig(column, 'number')) && (
                <ListItem style={{ backgroundColor: colorSystem.neutral.white }}>
                    <Flexbox
                        gap={8}
                        sx={{
                            flexDirection: 'column',
                        }}
                    >
                        <FilterFunctionMenuItem column={column} />
                        <FilterInNumberRange column={column} onClose={onClose} />
                    </Flexbox>
                </ListItem>
            )}
            {isPresent(getFilterConfig(column, 'monetaryValue')) && (
                <ListItem style={{ backgroundColor: colorSystem.neutral.white }}>
                    <Flexbox
                        gap={8}
                        sx={{
                            flexDirection: 'column',
                        }}
                    >
                        <FilterFunctionMenuItem column={column} />
                        <FilterInMonetaryValueRange column={column} onClose={onClose} />
                    </Flexbox>
                </ListItem>
            )}
            {isPresent(getFilterConfig(column, 'quantity')) && (
                <ListItem style={{ backgroundColor: colorSystem.neutral.white }}>
                    <Flexbox
                        gap={8}
                        sx={{
                            flexDirection: 'column',
                        }}
                    >
                        <FilterFunctionMenuItem column={column} />
                        <FilterInQuantityRange column={column} onClose={onClose} />
                    </Flexbox>
                </ListItem>
            )}
            {isPresent(getFilterConfig(column, 'date')) && (
                <DateColumnFilterContent column={column} onClose={onClose} />
            )}
            {isPresent(getFilterConfig(column, 'enum')) && (
                <ListItem style={{ backgroundColor: colorSystem.neutral.white }}>
                    <Flexbox
                        gap={8}
                        sx={{
                            flexDirection: 'column',
                        }}
                    >
                        <FilterFunctionMenuItem column={column} />
                        <FilterEnumEqualsAny column={column} sharedContext={sharedContext} />
                    </Flexbox>
                </ListItem>
            )}
            {isPresent(getFilterConfig(column, 'array')) && (
                <ListItem style={{ backgroundColor: colorSystem.neutral.white }}>
                    <Flexbox
                        gap={8}
                        sx={{
                            flexDirection: 'column',
                        }}
                    >
                        <FilterFunctionMenuItem column={column} />
                        <FilterArrIncludesSome column={column} sharedContext={sharedContext} />
                    </Flexbox>
                </ListItem>
            )}
        </Menu>
    );
}

function DateColumnFilterContent<TData extends RowData>({
    column,
    onClose,
}: {
    column: Column<TData, unknown>;
    onClose: () => void;
}): JSX.Element {
    const filterValue = column.getFilterValue();
    const activeFilterFn = isFilterValue(filterValue) ? filterValue.filterFn : SupportedFilterFn.inDateRange;

    return (
        <ListItem style={{ backgroundColor: colorSystem.neutral.white }}>
            <Flexbox
                gap={8}
                sx={{
                    flexDirection: 'column',
                }}
            >
                <FilterFunctionMenuItem column={column} />
                {activeFilterFn === SupportedFilterFn.isInLastDays ||
                activeFilterFn === SupportedFilterFn.isOlderThanDays ? (
                    <FilterInRelativeDateRange column={column} onClose={onClose} />
                ) : (
                    <FilterInDateRange column={column} onClose={onClose} />
                )}
            </Flexbox>
        </ListItem>
    );
}

function DeleteFilterMenuItem<TData extends RowData>({
    table,
    onClose,
}: {
    table: Table<TData>;
    onClose: () => void;
}): JSX.Element {
    const hasFiltered = table.getAllLeafColumns().some((col) => col.getIsFiltered());

    const handleDeleteFilter = () => {
        table
            .getAllLeafColumns()
            .filter((col) => col.getCanFilter())
            .forEach((col) => col.setFilterValue(undefined));
        onClose();
    };

    return (
        <MenuItem
            label={t`Clear filter`}
            variant={'destructive'}
            startIcon={<XIcon />}
            onClick={handleDeleteFilter}
            disabled={!hasFiltered}
        />
    );
}

const StyledButton = styled(TertiaryButton)({
    height: '16px',
    padding: '3px 3px',
    '& .MuiButton-endIcon ': {
        margin: '0px -2px 0px 2px',
    },
});

export function FilterFunctionMenuItem<TData extends RowData>({
    column,
}: {
    column: Column<TData, unknown>;
}): JSX.Element {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const filterConfig = assertPresent(column.columnDef.meta?.filterConfig);
    const defaultFilterFn = assertPresent(column.columnDef.meta?.filterConfig?.defaultFilterFn);

    const defaultFilterValue = {
        [SupportedFilterFn.includesString]: '',
        [SupportedFilterFn.isEmptyString]: '',
        [SupportedFilterFn.isNotEmptyString]: '',
        [SupportedFilterFn.inNumberRange]: [null, null],
        [SupportedFilterFn.inMonetaryValueRange]: [null, null],
        [SupportedFilterFn.inDateRange]: [null, null],
        [SupportedFilterFn.isInLastDays]: null,
        [SupportedFilterFn.isOlderThanDays]: null,
        [SupportedFilterFn.equalsAny]: [],
        [SupportedFilterFn.equalsNone]: [],
        [SupportedFilterFn.arrIncludesSome]: [],
        [SupportedFilterFn.arrIncludesAll]: [],
        [SupportedFilterFn.arrExcludesSome]: [],
        [SupportedFilterFn.inQuantityRange]: [null, null],
    };

    const unsafeFilterValue = column.getFilterValue();
    const { filterFn } = isFilterValue(unsafeFilterValue) ? unsafeFilterValue : { filterFn: defaultFilterFn };

    const supportedFilters = FilterFnByDataType[filterConfig.dataType];

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (newFilterFn: SupportedFilterFn) => {
        column.setFilterValue({ filterFn: newFilterFn, value: defaultFilterValue[newFilterFn] });
        return setAnchorEl(null);
    };

    return (
        <Flexbox
            gap={2}
            sx={{
                alignItems: 'baseline',
            }}
        >
            <Text variant={'body-small'} color={colorSystem.neutral[8]}>
                {`${column.columnDef.meta?.label()}`}
            </Text>
            <StyledButton
                size="small"
                disableElevation
                onClick={handleClick}
                endIcon={<ChevronDownIcon />}
                disabled={supportedFilters.length === 1 || !column.columnDef.meta?.enableAdvancedFilters}
            >
                {transEnum(filterFn, FilterFnOptionTranslations)}
            </StyledButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => handleClose(filterFn)}>
                {supportedFilters.map((filterFn) => (
                    <MenuItem
                        key={filterFn}
                        onClick={() => handleClose(filterFn)}
                        label={transEnum(filterFn, FilterFnOptionTranslations)}
                    />
                ))}
            </Menu>
        </Flexbox>
    );
}
